import type { BulkAction, Omittable } from "@salestrak/ui";

import type { CustomFieldRow } from "#app/lib/data";
import { settleAll } from "#app/lib/utils/settle-all";

/**
 * The one custom-field bulk roster, shared by its two mounts (the settings
 * page and the manage dialog) so the surfaces cannot drift. Both sit behind
 * `custom_fields.manage`, so Duplicate needs no gate of its own; Delete keeps
 * the `delete.items` interlock. Duplicate sits in the strip as an icon-only
 * button (label = tooltip), to the left of Delete.
 */
export function buildCustomFieldBulkActions({
  canDelete,
  deleteCustomField,
  duplicateCustomField,
  refetchList,
}: Omittable<{
  canDelete: boolean;
  /** Each must reject on a failure (`{ silent: true }`), so the bar reports
   *  the batch once. */
  deleteCustomField: (id: string, options: { silent: true }) => Promise<unknown>;
  duplicateCustomField: (id: string, options: { silent: true }) => Promise<unknown>;
  refetchList: () => Promise<unknown>;
}>): BulkAction<CustomFieldRow>[] {
  const actions: BulkAction<CustomFieldRow>[] = [
    {
      id: "duplicate",
      type: "direct",
      label: "Duplicate",
      icon: "copy",
      iconOnly: true,
      successMessage: (_rows, count) =>
        `${count} field${count !== 1 ? "s" : ""} duplicated`,
      onAction: async (selected) => {
        try {
          await settleAll(selected, (f) => duplicateCustomField(f.id, { silent: true }));
        } finally {
          await refetchList();
        }
      },
    },
  ];
  if (canDelete) {
    actions.push({
      id: "delete",
      type: "confirm",
      label: "Delete",
      dialogLabel: "Delete...",
      icon: "trash",
      destructive: true,
      confirmTitle: (rows) =>
        `Delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}?`,
      confirmDescription: (rows) =>
        `This will permanently delete ${rows.length} custom field${rows.length !== 1 ? "s" : ""}. They will no longer be available in any issues or forms. This cannot be undone.`,
      confirmLabel: "Delete Fields",
      onAction: async (selected) => {
        try {
          await settleAll(selected, (f) => deleteCustomField(f.id, { silent: true }));
        } finally {
          await refetchList();
        }
      },
    });
  }
  return actions;
}
